import { Tooltip } from '@chakra-ui/react';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import { Icons, defaultIconSize } from '../Icons';
import { SampleLifecycle } from './types';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';

export function SampleState({
  lifecycle,
  sampleReadyDate,
  handledNote,
}: {
  lifecycle: SampleLifecycle;
  sampleReadyDate: string | null;
  handledNote?: string | null;
}) {
  const { t } = useTranslation();
  const enumState = ensureEnumNumber(SampleLifecycle, lifecycle);

  const withNote = (label: string) =>
    handledNote ? `${label} — ${handledNote}` : label;

  switch (enumState) {
    case SampleLifecycle.Nothing: {
      return null;
    }
    case SampleLifecycle.SampleCancelled: {
      return (
        <Tooltip label={withNote(t('productApproval.cancelled'))}>
          <Icons.MinusCircleOutline
            boxSize={defaultIconSize}
            aria-label={t('productApproval.cancelled')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleRejected: {
      return (
        <Tooltip label={withNote(t('sample.rejected'))}>
          <Icons.Cancel
            boxSize={defaultIconSize}
            aria-label={t('sample.rejected')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleAccepted: {
      return (
        <Tooltip label={withNote(t('sample.accepted'))}>
          <Icons.CheckmarkDecagram
            boxSize={defaultIconSize}
            aria-label={t('sample.accepted')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleOnHold: {
      return (
        <Tooltip label={withNote(t('sample.onHold'))}>
          <Icons.PauseCircle
            boxSize={defaultIconSize}
            aria-label={t('sample.onHold')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleBeingPreparedAtSupplier: {
      return (
        <Tooltip
          label={
            sampleReadyDate
              ? t('sample.exptectedReadyDate', {
                  date: formatDate(Locale.En, sampleReadyDate),
                })
              : t('sample.beingPreparedAtSupplier')
          }
        >
          <Icons.Gift
            boxSize={defaultIconSize}
            aria-label={
              sampleReadyDate
                ? t('sample.exptectedReadyDate', {
                    date: formatDate(Locale.En, sampleReadyDate),
                  })
                : t('sample.beingPreparedAtSupplier')
            }
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleBeingPreparedReadySampleDatePassed: {
      return (
        <Tooltip
          label={
            sampleReadyDate
              ? t('sample.exptectedReadyDate', {
                  date: formatDate(Locale.En, sampleReadyDate),
                })
              : t('sample.beingPreparedAtSupplier')
          }
        >
          <Icons.Gift
            color="danger.200"
            boxSize={defaultIconSize}
            aria-label={
              sampleReadyDate
                ? t('sample.exptectedReadyDate', {
                    date: formatDate(Locale.En, sampleReadyDate),
                  })
                : t('sample.beingPreparedAtSupplier')
            }
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleReceivedFromSupplier: {
      return (
        <>
          <Tooltip label={t('sample.receivedFromSupplier')}>
            <Icons.GiftOpen
              boxSize={defaultIconSize}
              aria-label={t('sample.receivedFromSupplier')}
            />
          </Tooltip>
        </>
      );
    }
    case SampleLifecycle.SampleUnhandled: {
      return (
        <Tooltip label={t('sample.receivedInternally')}>
          <Icons.ArrowCollapseRight
            boxSize={defaultIconSize}
            aria-label={t('sample.receivedInternally')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleReceivedAtBrand: {
      return (
        <Tooltip label={t('sample.receivedAtBrand')}>
          <Icons.ArrowExpandDown
            boxSize={defaultIconSize}
            aria-label={t('sample.receivedAtBrand')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleLost: {
      return (
        <Tooltip label={t('sample.isLost')}>
          <Icons.CircleOpacity
            boxSize={defaultIconSize}
            aria-label={t('sample.isLost')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInBrandTransportRequestAwaitingShipment: {
      return (
        <Tooltip label={t('sample.inShipmentToBrand')}>
          <Icons.TruckFast
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipmentToBrand')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInBrandTransportRequestAwaitingDispatch: {
      return (
        <Tooltip label={t('sample.inShipmentToBrand')}>
          <Icons.TruckFast
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipmentToBrand')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInBrandTransportRequestDispatched: {
      return (
        <Tooltip label={t('sample.inShipmentToBrand')}>
          <Icons.TruckFast
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipmentToBrand')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInInternalTransportRequestAwaitingShipment: {
      return (
        <Tooltip label={t('sample.inShipment')}>
          <Icons.TruckDelivery
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipment')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInInternalTransportRequestAwaitingDispatch: {
      return (
        <Tooltip label={t('sample.inShipment')}>
          <Icons.TruckDelivery
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipment')}
          />
        </Tooltip>
      );
    }
    case SampleLifecycle.SampleInInternalTransportRequestDispatched: {
      return (
        <Tooltip label={t('sample.inShipment')}>
          <Icons.TruckDelivery
            boxSize={defaultIconSize}
            aria-label={t('sample.inShipment')}
          />
        </Tooltip>
      );
    }
  }
}
